import {useContext, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import {flushSync} from "react-dom";
import Currency from "@autocx/forms/src/controls/currency";
import WebsiteForm from "../../../components/website-form";
import ModalDialog from "../../../components/modal-dialog";
import {WebsiteContext} from "../../../index";
import useCdnUrl from "../../../hooks/cdn-hook";
import {aspectRatio} from "../../../utils/block-utils";

const CARD_ACTION_TYPES = {
    enquiry: {
        buttonLabel: "Enquire Now",
        getTitle: () => "Enquire",
    },
};

const PRODUCT_IMAGE_PLACEHOLDER = "https://dr1k2g3wmnols.cloudfront.net/images/image-placeholder.png";

ProductCardAction.propTypes = {
    product: PropTypes.object,
    className: PropTypes.string,
};

ProductCardAction.defaultProps = {
    product: null,
    className: "",
};

export function getProductCardActionConfig(product) {
    if (!product?.cardAction?.enabled) return null;

    const purchaseMethod = product?.cardAction?.purchaseMethod;
    const actionType = CARD_ACTION_TYPES[purchaseMethod];
    if (!actionType) return null;

    if (purchaseMethod === "enquiry" && !product?.cardAction?.form) return null;

    return {
        ...actionType,
        purchaseMethod,
        formUuid: product?.cardAction?.formUuid ?? null,
        form: product?.cardAction?.form ?? null,
    };
}

function getPrimaryProductImage(product) {
    return product?.images?.length > 0
        ? product?.imagesState?.[0]?.__cx__?.key || product?.images?.[0]
        : PRODUCT_IMAGE_PLACEHOLDER;
}

function ProductCardActionSummaryPrice({product}) {
    const context = useContext(WebsiteContext);

    if (product?.hidePriceOnWebsite) return null;

    return (
        <div className="mt-1 text-sm text-gray-900">
            {product?.discountedSellingPrice ? (
                <span className={"font-semibold mr-1"}>
                    <Currency.DisplayValue
                        currency={context.currency}
                        country={context.country}
                        value={product.discountedSellingPrice}
                        emptyPlaceholder={""}
                    />
                </span>
            ) : null}
            <span
                className={classNames(product?.discountedSellingPrice ? "line-through opacity-80 text-sm" : "font-semibold")}
            >
                {product?.sellingPrice
                    ? (
                        <Currency.DisplayValue
                            currency={context.currency}
                            country={context.country}
                            value={product.sellingPrice}
                            emptyPlaceholder={""}
                        />
                    )
                    : "Price on Application"}
            </span>
            {product?.sellingPriceDisclaimer ? (
                <span className="ml-1 text-gray-500 text-sm">
                    {product.sellingPriceDisclaimer}
                </span>
            ) : null}
        </div>
    );
}

function ProductCardSummaryImage({product, sizeClassName = "w-20"}) {
    const context = useContext(WebsiteContext);
    const imageAspectRatio = {values: {options: {imageAspectRatio: context?.website?.store?.productCardAspectRatio}}};
    const imageSrc = useCdnUrl(getPrimaryProductImage(product));

    return (
        <div className={classNames(sizeClassName, aspectRatio(imageAspectRatio), "shrink-0 overflow-hidden rounded-theme bg-gray-100")}>
            <img
                src={imageSrc}
                alt={product?.title ?? "Product image"}
                className="h-full w-full object-cover object-center"
            />
        </div>
    );
}

function ProductCardActionSummaryContent({product, titleClassName = "text-base font-semibold leading-6"}) {
    return (
        <div className="min-w-0 flex-1">
            <div className={classNames("line-clamp-2 text-gray-900", titleClassName)}>
                {product?.title ?? "This product"}
            </div>
            <ProductCardActionSummaryPrice product={product}/>
        </div>
    );
}

function ProductCardActionSummary({product}) {
    return (
        <div className="rounded-theme border border-gray-200 bg-gray-50 px-3 py-3">
            <div className="flex items-center gap-3">
                <ProductCardSummaryImage product={product} sizeClassName="w-20"/>
                <ProductCardActionSummaryContent product={product} titleClassName="text-sm font-semibold leading-5"/>
            </div>
        </div>
    );
}

function getFirstModalField(popup) {
    return popup?.querySelector(
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])'
    ) ?? null;
}

function focusElement(element, {forceRefocus = false} = {}) {
    if (!element) return false;

    if (forceRefocus && typeof document !== "undefined" && document.activeElement === element) {
        element.blur?.();
    }

    element.focus?.();
    if (typeof element.setSelectionRange === "function") {
        const valueLength = element.value?.length ?? 0;
        element.setSelectionRange(valueLength, valueLength);
    }

    return true;
}

function focusFirstModalField(options) {
    if (typeof document === "undefined") return false;

    const popup = document.querySelector("[data-popup]");
    return focusElement(getFirstModalField(popup), options);
}

function ProductCardActionModal({product, actionConfig, isOpen, onClose}) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [formInstanceKey, setFormInstanceKey] = useState(0);
    const [shouldResetFormOnClose, setShouldResetFormOnClose] = useState(false);
    const initialFormValues = useMemo(() => ({form: actionConfig.form}), [actionConfig.form]);
    const formValuesRef = useRef(initialFormValues);

    useEffect(() => {
        if (isOpen) return;
        setIsSubmitting(false);

        if (!shouldResetFormOnClose) return;

        formValuesRef.current = initialFormValues;
        setIsDirty(false);
        setFormInstanceKey((previous) => previous + 1);
        setShouldResetFormOnClose(false);
    }, [initialFormValues, isOpen, shouldResetFormOnClose]);

    const handleCloseRequest = () => {
        if (!isDirty || typeof window === "undefined") {
            onClose?.();
            return;
        }

        const shouldDiscard = window.confirm("Discard your enquiry?");
        if (!shouldDiscard) return;

        setIsDirty(false);
        setShouldResetFormOnClose(true);
        onClose?.();
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={handleCloseRequest}
            canClose={!isSubmitting}
            title={actionConfig?.getTitle?.(product)}
            titleClassName="sr-only !mb-0"
            getInitialFocusElement={getFirstModalField}
            autoFocusOnMount={!actionConfig?.openedWithTouch}
            panelClassName="sm:w-[34rem]"
        >
            <div className="w-full">
                <div className="mb-5">
                    <h2 className="!mb-0 !text-2xl text-left">
                        {actionConfig?.getTitle?.(product)}
                    </h2>
                    <div className="mt-4">
                        <ProductCardActionSummary product={product}/>
                    </div>
                    <div className="mt-5"/>
                </div>
                {!actionConfig?.form && (
                    <div className="py-4 text-sm text-gray-600">
                        There was a problem loading this action.
                    </div>
                )}
                {actionConfig?.form && actionConfig?.purchaseMethod === "enquiry" && (
                    <WebsiteForm
                        key={formInstanceKey}
                        values={{form: actionConfig.form}}
                        initialValues={formValuesRef.current}
                        product={{id: product?.uuid, workspaceId: product?.workspaceId}}
                        label="Send Enquiry"
                        submitButtonClassName="w-full justify-center"
                        onValuesChange={(values) => {
                            formValuesRef.current = values;
                        }}
                        onFieldChange={() => setIsDirty(true)}
                        onSubmittingChange={setIsSubmitting}
                        onSubmitted={() => {
                            setIsDirty(false);
                            setShouldResetFormOnClose(true);
                        }}
                    />
                )}
            </div>
        </ModalDialog>
    );
}

function ProductCardAction({product, className}) {
    const actionConfig = useMemo(() => getProductCardActionConfig(product), [product]);
    const [isOpen, setIsOpen] = useState(false);
    const [openedWithTouch, setOpenedWithTouch] = useState(false);

    useEffect(() => {
        if (isOpen) return;
        setOpenedWithTouch(false);
    }, [isOpen]);

    const openModal = ({fromTouch = false} = {}) => {
        flushSync(() => {
            setOpenedWithTouch(fromTouch);
            setIsOpen(true);
        });
    };

    const handlePrepareOpen = () => {
        if (isOpen) return;
        openModal({fromTouch: true});
    };

    const handleOpen = (event) => {
        if (!isOpen) {
            openModal();
        }
        focusFirstModalField({forceRefocus: event?.type === "click" || event?.type === "touchend"});
    };

    if (!actionConfig) return null;

    return (
        <>
            <button
                type="button"
                data-action=""
                onTouchStart={handlePrepareOpen}
                onTouchEnd={handleOpen}
                onClick={handleOpen}
                style={{touchAction: "manipulation"}}
                className={classNames("button w-full justify-center", className)}
            >
                {actionConfig.buttonLabel}
            </button>
            <ProductCardActionModal
                product={product}
                actionConfig={{...actionConfig, openedWithTouch}}
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
            />
        </>
    );
}

export default ProductCardAction;
